import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BusinessIcon from '@mui/icons-material/Business';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function ControlCenterClientFilter({ clients = [], selectedClients = [], onChange }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const open = Boolean(anchorEl);

  const selectedIds = React.useMemo(
    () => new Set(selectedClients.map((c) => String(c.id))),
    [selectedClients],
  );

  const filteredClients = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = query
      ? clients.filter((c) => (c.client_name || '').toLowerCase().includes(query))
      : clients;
    return [...list].sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
  }, [clients, search]);

  const toggleClient = (client) => {
    const idStr = String(client.id);
    if (selectedIds.has(idStr)) {
      onChange(selectedClients.filter((c) => String(c.id) !== idStr));
    } else {
      onChange([...selectedClients, { id: client.id, client_name: client.client_name }]);
    }
  };

  const label = selectedClients.length === 0
    ? 'All clients'
    : selectedClients.length === 1
      ? selectedClients[0].client_name
      : `${selectedClients.length} clients`;

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<BusinessIcon sx={{ fontSize: 15 }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: 'none',
          color: selectedClients.length > 0 ? 'text.primary' : '#8e8e93',
          fontWeight: selectedClients.length > 0 ? 600 : 500,
          fontSize: 13,
          px: 1,
          minWidth: 'auto',
          maxWidth: 220,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, mt: 0.5, width: 260 } } }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          {selectedClients.length > 0 && (
            <Button
              size="small"
              onClick={() => onChange([])}
              sx={{ textTransform: 'none', fontSize: 12.5, mb: 0.5 }}
            >
              Clear all ({selectedClients.length})
            </Button>
          )}
          <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
            {filteredClients.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', px: 1, py: 1 }}>
                No clients found
              </Typography>
            ) : (
              filteredClients.map((client) => {
                const checked = selectedIds.has(String(client.id));
                return (
                  <ListItemButton
                    key={client.id}
                    onClick={() => toggleClient(client)}
                    selected={checked}
                    sx={{ borderRadius: 1.5, py: 0.25, px: 0.75 }}
                  >
                    <Checkbox checked={checked} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                    <ListItemText
                      primary={client.client_name || 'Unknown'}
                      primaryTypographyProps={{ fontSize: 13.5 }}
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
}
